import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import Icon from '../components/ui/Icons';
import ContactRequestForm from '../components/ui/ContactRequestForm';
import useReveal from '../components/ui/Reveal';
import COMPANY, { ROUTES } from '../config/company';
import { Faq } from './Services';

import heroImage from '../assets/Images/about-group-photo.webp';

const CHANNELS = [
  {
    icon: 'phone',
    label: 'Call us',
    value: COMPANY.phoneDisplay,
    href: COMPANY.phone,
    note: 'Ask about trucking, shipment details, service availability and freight inquiries.',
    cta: 'Call now',
    primary: true,
  },
  {
    icon: 'mail',
    label: 'Email',
    value: COMPANY.emailDisplay,
    href: COMPANY.email,
    note: 'Best for lane questions, freight details and written inquiries. Do not email sensitive documents without arrangements.',
    cta: 'Send an email',
  },
  {
    icon: 'message',
    label: 'SMS updates',
    value: 'SMS preferences',
    href: ROUTES.smsOptIn,
    note: 'Opt in to customer care and/or marketing text messages on our dedicated SMS consent page. Consent is optional.',
    cta: 'SMS opt-in page',
    internal: true,
  },
];

export default function Contact() {
  useReveal();

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's talk about your freight."
        lede={`Have a trucking or freight question? Call or email our team, or send the contact form below. A Bitnex Freight LLC representative normally replies ${COMPANY.responseTime.toLowerCase()}.`}
        image={heroImage}
        compact
      />

      <section className="py-16 sm:py-20">
        <div className="shell">
          <div className="grid gap-4 lg:grid-cols-3 lg:gap-5">
            {CHANNELS.map((c, i) => {
              const cls = [
                'reveal group flex flex-col rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1.5',
                c.primary ? 'texture-dark bg-navy-900 text-white hover:shadow-lift' : 'card hover:shadow-lift',
              ].join(' ');
              const inner = (
                <>
                  <span className={`mb-5 grid h-12 w-12 place-items-center rounded-lg ${c.primary ? 'bg-gold-300 text-navy-900' : 'bg-navy-900 text-gold-300'}`}>
                    <Icon name={c.icon} className="h-5 w-5" />
                  </span>
                  <div className={`text-[0.72rem] font-bold uppercase tracking-[0.16em] ${c.primary ? 'text-gold-300' : 'text-ink-500'}`}>{c.label}</div>
                  <div className="mt-1 break-words font-display text-2xl font-bold">{c.value}</div>
                  <p className={`mt-2.5 flex-1 text-[0.88rem] leading-relaxed ${c.primary ? 'text-navy-100' : 'text-ink-600'}`}>{c.note}</p>
                  <span className={`mt-5 inline-flex items-center gap-1.5 text-[0.9rem] font-bold ${c.primary ? 'text-gold-300' : 'text-navy-800'}`}>
                    {c.cta}
                    <Icon name="arrowRight" className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </>
              );
              return c.internal ? (
                <Link key={c.label} to={c.href} className={cls} style={{ '--reveal-delay': `${i * 80}ms` }}>{inner}</Link>
              ) : (
                <a key={c.label} href={c.href} className={cls} style={{ '--reveal-delay': `${i * 80}ms` }}>{inner}</a>
              );
            })}
          </div>

          <div className="mt-12 grid gap-10 lg:grid-cols-[1.3fr_1fr]">
            {/* Shared working contact and SMS form; form backend is unchanged. */}
            <ContactRequestForm mode="contact" />

            {/* DETAILS */}
            <aside className="space-y-5">
              <div className="card reveal p-7" style={{ '--reveal-delay': '80ms' }}>
                <h3 className="font-display text-xl font-bold">Contact details</h3>
                <div className="mt-4 space-y-5 text-[0.92rem]">
                  <div className="flex gap-3">
                    <Icon name="pin" className="mt-0.5 h-5 w-5 shrink-0 text-gold-600" />
                    <div>
                      <div className="font-bold text-navy-900">{COMPANY.hq.label}</div>
                      <div className="text-ink-600">{COMPANY.hq.line1}<br />{COMPANY.hq.line2}</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Icon name="clock" className="mt-0.5 h-5 w-5 shrink-0 text-gold-600" />
                    <div>
                      <div className="font-bold text-navy-900">Hours</div>
                      <div className="text-ink-600">{COMPANY.hours}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card reveal texture-dark bg-navy-900 p-7 text-white" style={{ '--reveal-delay': '160ms' }}>
                <h3 className="font-display text-xl font-bold text-white">Have a load or carrier inquiry?</h3>
                <p className="mt-2 text-[0.9rem] text-navy-100">
                  Share your lane, freight type and timing. Our team can review the details and explain next steps.
                </p>
                <Link to={ROUTES.signup} className="btn-primary mt-5 w-full">
                  Carrier inquiry <Icon name="arrowRight" className="h-4 w-4" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Faq title="Before you message — these might answer it" />
    </>
  );
}
